import { useEffect, useMemo, useState } from 'react';
import { MapContainer } from 'react-leaflet';
import MapWithNavigation from '../../home/MapWithNavigation';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  TripLeg,
  TripLegAddress,
} from '../../../data/models/reservation.model';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import * as driverSlice from '../../../data/driver.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import {
  TripLegStatus,
  tripStatusWithEtaList,
} from '../../../data/models/trip-leg-status.model';
import { DriverLocation } from '../../../data/models/driver-location.model';

const PREFIX = 'MemberTripLegLiveTracking';

const classes = {
  container: `${PREFIX}-container`,
  map: `${PREFIX}-map`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    minWidth: 170,
    flex: 1,
    width: '100%',
  },
  [`& .${classes.map}`]: {
    minHeight: 233,
    height: '100%',
    width: '100%',
    '& div.leaflet-routing-alternatives-container': {
      display: 'none',
    },
  },
}));

/**
 * Validates if driver location data is valid and usable for display
 */
const isValidDriverLocation = (location: DriverLocation | null): boolean => {
  if (!location) return false;

  const lat = parseFloat(location.latitude);
  const lng = parseFloat(location.longitude);

  return !!(lat && lng && location.driverID);
};

/**
 * Checks if trip leg status should show live tracking with driver location
 */
const shouldShowLiveTracking = (tripLeg: TripLeg): boolean => {
  return tripStatusWithEtaList.includes(tripLeg.status as TripLegStatus);
};

const MemberTripLegLiveTracking = ({ tripLeg }: { tripLeg: TripLeg }) => {
  const dispatch = useAppDispatch();

  const [tripEndPoints, setTripEndPoints] = useState<{
    pickupAddress: TripLegAddress;
    dropoffAddress: TripLegAddress;
  }>();
  const [center, setCenter] = useState({
    lat: 0,
    lng: 0,
  });

  const driverLocation = useAppSelector(driverSlice.selectDriverLocation);
  const loadingDriverLocation = useAppSelector(
    driverSlice.selectLoadingDriverLocation,
  );
  const loadedDriverLocation = useAppSelector(
    driverSlice.selectLoadedDriverLocation,
  );

  const isLiveTrip = useMemo(() => {
    return shouldShowLiveTracking(tripLeg);
  }, [tripLeg.status]);

  const validDriverLocation = useMemo(() => {
    if (!isLiveTrip || !loadedDriverLocation || loadingDriverLocation) {
      return null;
    }
    return isValidDriverLocation(driverLocation) ? driverLocation : null;
  }, [driverLocation, isLiveTrip, loadedDriverLocation, loadingDriverLocation]);

  useEffect(() => {
    if (tripLeg) {
      setCenter({
        lat: tripLeg.pickUpAddress.latitude || 0,
        lng: tripLeg.pickUpAddress.longitude || 0,
      });
      setTripEndPoints({
        pickupAddress: tripLeg.pickUpAddress,
        dropoffAddress: tripLeg.dropOffAddress,
      });
    }
  }, [dispatch, tripLeg]);

  return (
    <StyledBox className={classes.container}>
      <MapContainer
        className={classes.map}
        zoom={11}
        center={{
          lat: tripLeg?.pickUpAddress?.latitude || 0,
          lng: tripLeg?.pickUpAddress?.longitude || 0,
        }}>
        <MapWithNavigation
          tripLegs={tripEndPoints ? [tripEndPoints] : []}
          driverLocation={validDriverLocation}
        />
      </MapContainer>
    </StyledBox>
  );
};

export default MemberTripLegLiveTracking;
